import React, { useMemo, useEffect, useState } from 'react';
import { Card, Text, Center, Avatar } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import type { EmblaCarouselType } from 'embla-carousel';
import { IconPhoto } from '@tabler/icons-react';
import classes from './PostCard.module.css';
import '@mantine/carousel/styles.css';
import type PostInterface from '../../interfaces/PostInterface.ts';
import { trackEvent } from '../../utils/analytics.ts';

type Props = {
  post: PostInterface;
  height?: number;
  width?: number | string;
  radius?: number;
  hrefBase?: string; // default 'post'
};

export default function PostCard({
  post,
  height = 420,
  width = '100%',
  radius = 24,
  hrefBase = 'post',
}: Props) {
  const { postId, title, description, images, coverImageId } = post;

  // Sort by 'order' (ascending). If 'order' is missing or equal, keep original order.
  const orderedImages = useMemo(() => {
    if (!images?.length) return [];
    return [...images].sort((a, b) => {
      const ao = Number.isFinite(a.order) ? a.order : Number.POSITIVE_INFINITY;
      const bo = Number.isFinite(b.order) ? b.order : Number.POSITIVE_INFINITY;
      if (ao !== bo) return ao - bo;
      // stable-ish fallback by imageId to avoid jitter if same order
      return String(a.imageId).localeCompare(String(b.imageId));
    });
  }, [images]);

  const initialIndex = useMemo(() => {
    if (!orderedImages.length) return 0;
    if (!coverImageId) return 0;
    const idx = orderedImages.findIndex((i) => i.imageId === coverImageId);
    return idx >= 0 ? idx : 0;
  }, [orderedImages, coverImageId]);

  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);

  useEffect(() => {
    if (embla) {
      try {
        embla.scrollTo(initialIndex);
      } catch {}
    }
  }, [embla, initialIndex]);

  const href = `/${hrefBase}/${postId}`.replace(/\/+/, '/');

  const goToPost = () => {
    trackEvent('post_card_open', { category: 'Listings', label: postId, value: idxSafe(initialIndex) });
    if (typeof window !== 'undefined') window.location.href = href;
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToPost();
    }
  };

  return (
    <Card
      className={classes.postCard}
      shadow="md"
      h={height}
      w={width}
      radius={radius}
      padding="xl"
      aria-label={title || 'Property card'}
    >
      <Card.Section>
        {orderedImages.length > 0 ? (
          <Carousel
            withIndicators
            className={classes.carousel}
            initialSlide={initialIndex}
            height={240}
            slideGap="xs"
            getEmblaApi={setEmbla}
            emblaOptions={{ loop: true }}
          >
            {orderedImages.map((img) => (
              <Carousel.Slide key={img.imageId}>
                <img src={img.url} alt={title} className={classes.carouselImage} />
              </Carousel.Slide>
            ))}
          </Carousel>
        ) : (
          <Center className={classes.imageFallback}>
            <Avatar radius="sm" size={64}>
              <IconPhoto size={24} />
            </Avatar>
          </Center>
        )}
      </Card.Section>

      <Card.Section
        className={`${classes.body} ${classes.bodyClickable}`}
        role="link"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={goToPost}
      >
        <Text fw={600} size="lg" mt="md" className={classes.title}>
          {title || 'Untitled post'}
        </Text>
        <Text mt="xs" c="dimmed" size="sm" className={classes.description}>
          {description || 'Add a nice description to attract visitors to this property.'}
        </Text>
        <Text mt="sm" fz="sm" className={classes.linkLike}>View details →</Text>
      </Card.Section>
    </Card>
  );
}

const idxSafe = (index: number) => Number.isFinite(index) ? index + 1 : undefined;
